// src/components/pages/PaymentHistoryPage/PaymentHistoryPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../services/useUserData';
import { apiService } from '../../services/api_url';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { BackIcon } from '../../assets/images/BackIcon';
import './PaymentHistoryPage.scss';

interface PaymentHistoryItem {
    id: string;
    amount: number;
    label_sub: string;
    payment_time: string;
    status: string;
    operation_type: 'subscription' | 'extend';
    months: number;
    devices_count: number;
    description: string;
}

type DateFilter = 'all' | 'today' | 'week' | 'month' | 'custom';

function PaymentHistoryPage() {
    const navigate = useNavigate();
    const { telegramId, loading: userLoading } = useUserData();
    const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
    const [filteredPayments, setFilteredPayments] = useState<PaymentHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dateFilter, setDateFilter] = useState<DateFilter>('all');
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const [showFilters, setShowFilters] = useState(false);
    const [statusFilter, setStatusFilter] = useState<'all' | 'subscription' | 'extend'>('all');

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
    }, []);

    useEffect(() => {
        if (!userLoading && telegramId) {
            loadPaymentHistory();
        }
    }, [userLoading, telegramId]);

    useEffect(() => {
        filterPayments();
    }, [payments, dateFilter, customStartDate, customEndDate, statusFilter]);

    const loadPaymentHistory = async () => {
        if (!telegramId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await apiService.getUserPayments(telegramId);
            if (response.success) {
                setPayments(response.payments);
            } else {
                setError('Не удалось загрузить историю операций');
            }
        } catch (err) {
            setError('Ошибка при загрузке истории операций');
        } finally {
            setLoading(false);
        }
    };

    const filterPayments = () => {
        let filtered = [...payments];

        // Фильтр по статусу операции
        if (statusFilter !== 'all') {
            filtered = filtered.filter(p => p.operation_type === statusFilter);
        }

        // Фильтр по дате
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        switch (dateFilter) {
            case 'today':
                filtered = filtered.filter(p => {
                    const paymentDate = new Date(p.payment_time);
                    return paymentDate >= today;
                });
                break;
            case 'week':
                const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                filtered = filtered.filter(p => new Date(p.payment_time) >= weekAgo);
                break;
            case 'month':
                const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                filtered = filtered.filter(p => new Date(p.payment_time) >= monthAgo);
                break;
            case 'custom':
                if (customStartDate) {
                    const start = new Date(customStartDate);
                    start.setHours(0, 0, 0, 0);
                    filtered = filtered.filter(p => new Date(p.payment_time) >= start);
                }
                if (customEndDate) {
                    const end = new Date(customEndDate);
                    end.setHours(23, 59, 59, 999);
                    filtered = filtered.filter(p => new Date(p.payment_time) <= end);
                }
                break;
            default:
                break;
        }

        setFilteredPayments(filtered);
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount: number): string => {
        return `${amount.toFixed(2)} ₽`;
    };

    const getStatusBadge = (status: string, operationType: string) => {
        if (operationType === 'extend') {
            return (
                <span className="payment-status-badge extend">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 8v4l3 3M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    </svg>
                    Продление
                </span>
            );
        }
        return (
            <span className="payment-status-badge subscription">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                Подписка
            </span>
        );
    };

    const getOperationIcon = (type: string) => {
        if (type === 'extend') {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 8v4l3 3M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    <path d="M12 6v2M12 12v2" />
                </svg>
            );
        }
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                <circle cx="12" cy="12" r="3" />
            </svg>
        );
    };

    const resetFilters = () => {
        setDateFilter('all');
        setStatusFilter('all');
        setCustomStartDate('');
        setCustomEndDate('');
    };

    if (userLoading || loading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="payment-history-page loading">
                    <div className="loader">
                        <Loader />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="payment-history-page">
                <div className="payment-history-header">
                    <button className="back-button" onClick={() => navigate('/profile')}>
                        <BackIcon />
                    </button>
                    <h1 className="payment-history-title">История операций</h1>
                    <button
                        className={`filter-toggle ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
                        </svg>
                    </button>
                </div>

                {showFilters && (
                    <div className="filters-panel">
                        <div className="filter-section">
                            <div className="filter-label">Период</div>
                            <div className="filter-buttons">
                                <button
                                    className={`filter-btn ${dateFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setDateFilter('all')}
                                >
                                    Все
                                </button>
                                <button
                                    className={`filter-btn ${dateFilter === 'today' ? 'active' : ''}`}
                                    onClick={() => setDateFilter('today')}
                                >
                                    Сегодня
                                </button>
                                <button
                                    className={`filter-btn ${dateFilter === 'week' ? 'active' : ''}`}
                                    onClick={() => setDateFilter('week')}
                                >
                                    Неделя
                                </button>
                                <button
                                    className={`filter-btn ${dateFilter === 'month' ? 'active' : ''}`}
                                    onClick={() => setDateFilter('month')}
                                >
                                    Месяц
                                </button>
                                <button
                                    className={`filter-btn ${dateFilter === 'custom' ? 'active' : ''}`}
                                    onClick={() => setDateFilter('custom')}
                                >
                                    Указать свой
                                </button>
                            </div>

                            {dateFilter === 'custom' && (
                                <div className="custom-date-range">
                                    <span className="date-separator">Начало</span>
                                    <input
                                        type="date"
                                        className="date-input"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        placeholder="Начальная дата"
                                    />
                                    <span className="date-separator">Конец</span>
                                    <input
                                        type="date"
                                        className="date-input"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        placeholder="Конечная дата"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="filter-section">
                            <div className="filter-label">Тип операции</div>
                            <div className="filter-buttons">
                                <button
                                    className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setStatusFilter('all')}
                                >
                                    Все
                                </button>
                                <button
                                    className={`filter-btn ${statusFilter === 'subscription' ? 'active' : ''}`}
                                    onClick={() => setStatusFilter('subscription')}
                                >
                                    Подписки
                                </button>
                                <button
                                    className={`filter-btn ${statusFilter === 'extend' ? 'active' : ''}`}
                                    onClick={() => setStatusFilter('extend')}
                                >
                                    Продления
                                </button>
                            </div>
                        </div>

                        {(dateFilter !== 'all' || statusFilter !== 'all') && (
                            <button className="reset-filters-btn" onClick={resetFilters}>
                                Сбросить фильтры
                            </button>
                        )}
                    </div>
                )}

                {error ? (
                    <div className="payment-history-error">
                        <div className="error-icon">❌</div>
                        <p>{error}</p>
                        <button onClick={loadPaymentHistory} className="retry-btn">
                            Повторить
                        </button>
                    </div>
                ) : filteredPayments.length === 0 ? (
                    <div className="payment-history-empty">
                        <div className="empty-icon">📭</div>
                        <h3>Операции не найдены</h3>
                        <p>
                            {payments.length === 0
                                ? 'У вас пока нет успешных платежей'
                                : 'Нет операций, соответствующих выбранным фильтрам'}
                        </p>
                        {payments.length > 0 && (
                            <button onClick={resetFilters} className="reset-filters-empty-btn">
                                Сбросить фильтры
                            </button>
                        )}
                        {payments.length === 0 && (
                            <button onClick={() => navigate('/payment')} className="go-to-payment-btn">
                                Перейти к оплате
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="payment-stats">
                            <span className="stats-count">Найдено: {filteredPayments.length} операций</span>
                            <span className="stats-total">
                                Сумма: {formatAmount(filteredPayments.reduce((sum, p) => sum + p.amount, 0))}
                            </span>
                        </div>

                        <div className="payment-history-list">
                            {filteredPayments.map((payment) => (
                                <div key={payment.id} className="payment-history-item">
                                    <div className="payment-history-item-icon">
                                        {getOperationIcon(payment.operation_type)}
                                    </div>

                                    <div className="payment-history-item-content">
                                        <div className="payment-history-item-header">
                                            <div className="payment-history-item-left">
                                                <span className="payment-history-item-description">
                                                    {payment.description}
                                                </span>
                                                {getStatusBadge(payment.status, payment.operation_type)}
                                            </div>
                                            <span className="payment-history-item-amount">
                                                {formatAmount(payment.amount)}
                                            </span>
                                        </div>

                                        <div className="payment-history-item-details">
                                            <span className="payment-history-item-date">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                                {formatDate(payment.payment_time)}
                                            </span>
                                            <span className="payment-history-item-id">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path d="M4 4h16v16H4z" />
                                                    <line x1="8" y1="8" x2="16" y2="8" />
                                                    <line x1="8" y1="12" x2="16" y2="12" />
                                                    <line x1="8" y1="16" x2="12" y2="16" />
                                                </svg>
                                                ID: {payment.id.slice(0, 8)}...
                                            </span>
                                            {payment.months && (
                                                <span className="payment-history-item-period">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <polyline points="12 6 12 12 16 14" />
                                                    </svg>
                                                    {payment.months} мес
                                                </span>
                                            )}
                                            {payment.devices_count && (
                                                <span className="payment-history-item-devices">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <rect x="2" y="3" width="20" height="14" rx="2" />
                                                        <line x1="8" y1="21" x2="16" y2="21" />
                                                        <line x1="12" y1="17" x2="12" y2="21" />
                                                    </svg>
                                                    {payment.devices_count} уст
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default PaymentHistoryPage;